/**
 * JsonViewer Component
 *
 * Lightweight JSON viewer with syntax highlighting
 */

import React, { useRef } from 'react';

interface JsonViewerProps {
  data: any;
}

export const JsonViewer: React.FC<JsonViewerProps> = ({ data }) => {
  // Use a ref to generate unique keys across recursive calls
  const keyCounter = useRef(0);
  const getKey = (prefix: string) => `${prefix}-${keyCounter.current++}`;

  const formatValue = (value: any, indent: number = 0, seen: WeakSet<object> = new WeakSet()): React.ReactNode[] => {
    const indentStr = '  '.repeat(indent);
    const elements: React.ReactNode[] = [];

    // Limit max depth to prevent stack overflow
    if (indent > 20) {
      elements.push(<span key={getKey('depth')} className="text-gray-500">...</span>);
      return elements;
    }

    if (value === null) {
      elements.push(<span key={getKey('null')} className="text-gray-500">null</span>);
    } else if (typeof value === 'boolean') {
      elements.push(<span key={getKey('bool')} className="text-purple-600">{String(value)}</span>);
    } else if (typeof value === 'number') {
      elements.push(<span key={getKey('num')} className="text-blue-600">{value}</span>);
    } else if (typeof value === 'string') {
      elements.push(<span key={getKey('str')} className="text-green-600">"{value}"</span>);
    } else if (Array.isArray(value)) {
      // Check for circular reference
      if (seen.has(value)) {
        elements.push(<span key={getKey('circular')} className="text-gray-500">[Circular]</span>);
        return elements;
      }
      seen.add(value);

      elements.push(<span key={getKey('arr-open')} className="text-gray-700">[</span>);
      if (value.length > 0) {
        elements.push(<br key={getKey('arr-br')} />);
        value.forEach((item, i) => {
          elements.push(<span key={getKey('arr-indent')}>{indentStr}  </span>);
          elements.push(...formatValue(item, indent + 1, seen));
          if (i < value.length - 1) {
            elements.push(<span key={getKey('arr-comma')} className="text-gray-700">,</span>);
          }
          elements.push(<br key={getKey('arr-br')} />);
        });
        elements.push(<span key={getKey('arr-indent-close')}>{indentStr}</span>);
      }
      elements.push(<span key={getKey('arr-close')} className="text-gray-700">]</span>);
    } else if (typeof value === 'object') {
      // Check for circular reference
      if (seen.has(value)) {
        elements.push(<span key={getKey('circular')} className="text-gray-500">[Circular]</span>);
        return elements;
      }
      seen.add(value);

      const keys = Object.keys(value);
      elements.push(<span key={getKey('obj-open')} className="text-gray-700">{'{'}</span>);
      if (keys.length > 0) {
        elements.push(<br key={getKey('obj-br')} />);
        keys.forEach((objKey, i) => {
          elements.push(<span key={getKey('obj-indent')}>{indentStr}  </span>);
          elements.push(<span key={getKey('obj-key')} className="text-cyan-700">"{objKey}"</span>);
          elements.push(<span key={getKey('obj-colon')} className="text-gray-700">: </span>);
          elements.push(...formatValue(value[objKey], indent + 1, seen));
          if (i < keys.length - 1) {
            elements.push(<span key={getKey('obj-comma')} className="text-gray-700">,</span>);
          }
          elements.push(<br key={getKey('obj-br')} />);
        });
        elements.push(<span key={getKey('obj-indent-close')}>{indentStr}</span>);
      }
      elements.push(<span key={getKey('obj-close')} className="text-gray-700">{'}'}</span>);
    }

    return elements;
  };

  // Reset counter on each render
  keyCounter.current = 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
      <pre className="text-sm font-mono leading-relaxed whitespace-pre-wrap break-all">
        <>{formatValue(data)}</>
      </pre>
    </div>
  );
};
